import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AssignPreparerService } from './assign-preparer.service';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-assign-preparer',
    templateUrl: './assign-preparer.component.html',
    styleUrls: ['./assign-preparer.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AssignPreparerComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // Standard component properties
  currentTabName = "Assign";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;

  // Modal properties
  modalTitle = "Assign Preparer";
  showFormLoader = true;

  // Data properties
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};
  gridData: any[] = [];
  passgriddata: any[] = [];

  // Template management
  templates: any[] = [];
  template_selection: any[] = [];
  selectedTemplate: any = null;
  display_template: any = null;
  display_template_copy: any = null;

  // Country and State data
  allCountries: any[] = [];
  allUSStates: any[] = [];
  default_country: any = {};

  // Selected row management
  selectedRowIndex: number = -1;
  selectedRow: any = {};
  saveBtn = true;
  saveloader = false;

  // LEID management
  leid_list: any[] = [];
  combination_key_List: any[] = [];

  // Dropdown settings
  dropdownSetting = {
    enableSearch: true,
    selectionLimit: 1,
    scrollableHeight: '200px',
    scrollable: true,
    showUncheckAll: false,
    showCheckAll: false,
    smartButtonTextConverter: function(itemText: any, originalItem: any) {
      return itemText;
    },
    smartButtonTextProvider: function(selectionArray: any[]) {
      return selectionArray[0]?.label || "Select";
    }
  };

  // Self Employed options
  selfEmployed = [
    { id: '1', name: 'Yes' },
    { id: '2', name: 'No' }
  ];

  // Scenario and issue properties
  scenario: string = '';
  globalScenarioDesc: string = '';
  glbl_issue_short_desc: string = '';
  glbl_issue_id: string = '';

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private assignPreparerService: AssignPreparerService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) { }

  @Input('grid-data')
  set _grid_data(gridData: string) {
    try {
      let data = safeJsonInput<any>(gridData, null, 'assign-preparer.parse1');
      if (data && data.length > 0) {
        this.gridData = data;
        this.passgriddata = _.cloneDeep(data);
        this.tryInit();
      }
    } catch (e) {
      console.log("Grid data input not yet stable. Still waiting to get resolved.");
    }
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();

    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));

    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);

    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
  }

  private resolveApiBase(): string {
    return this.GENERAL_CONFIG?.base_url || '';
  }

  private tryInit(): void {
    const baseOk = !!this.resolveApiBase();
    if (!baseOk || !this.passgriddata || this.passgriddata.length === 0) return;
    this.init();
  }

  init() {
    try {
      if (this.passgriddata && this.passgriddata.length > 0) {
        // Set scenario and other global properties
        this.scenario = this.globalService?.scenario || '';
        this.globalScenarioDesc = this.passgriddata[0]?.data?.SCENARIO_DESC || '';
        this.glbl_issue_short_desc = this.globalService?.issue_short_desc || '';
        this.glbl_issue_id = this.globalService?.issue_id || '';

        // Build LEID list
        this.leid_list = [];
        this.passgriddata.forEach((row: any) => {
          const temp_data = {
            leid: row.data?.HO_LEID,
            combination_key: row.data?.COMBINATION_KEY,
            reporting_period: row.data?.HO_REPORTING_PERIOD,
            group_obj_key: row.data?.GROUP_OBJ_KEY
          };
          this.leid_list.push(temp_data);
        });

        // Load data
        this.fetchCountryList().then(() => {
          this.fetchUSStateList().then(() => {
            this.fetchTemplates();
          });
        });
      }
    } catch (e) {
      console.error('Error in assignPreparer init:', e);
      this.showFormLoader = false;
    }
  }

  fetchCountryList(): Promise<void> {
    return new Promise((resolve, reject) => {
      const params = {};
      this.assignPreparerService.getCountries(
        this.resolveApiBase(),
        params,
        this.globalService
      ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (response: any) => {
          this.allCountries = [];
          if (response.jsonObject) {
            response.jsonObject.forEach((country: any) => {
              this.allCountries.push({
                id: country.COUNTRY_CODE,
                label: country.COUNTRY_DESC
              });
              if (country.COUNTRY_CODE === 'US') {
                this.default_country = {
                  id: country.COUNTRY_CODE,
                  label: country.COUNTRY_DESC
                };
              }
            });
          }
          resolve();
        },
        (error: any) => {
          console.error('Error fetching countries:', error);
          this._snackBar.open('Error loading country list', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          reject(error);
        }
      );
    });
  }

  fetchUSStateList(): Promise<void> {
    return new Promise((resolve, reject) => {
      const params = {};
      this.assignPreparerService.getUSStates(
        this.resolveApiBase(),
        params,
        this.globalService
      ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (response: any) => {
          this.allUSStates = [];
          if (response.jsonObject) {
            response.jsonObject.forEach((state: any) => {
              this.allUSStates.push({
                id: state.STATE_CODE,
                label: state.STATE_DESC
              });
            });
          }
          resolve();
        },
        (error: any) => {
          console.error('Error fetching states:', error);
          this._snackBar.open('Error loading state list', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          reject(error);
        }
      );
    });
  }

  fetchTemplates(): void {
    const params = {
      tax_year: this.globalService?.tax_year || this.passgriddata[0]?.data?.TAX_YEAR,
      scenario: this.filterParams?.scenario || this.globalService?.scenario,
      jcd_key: this.globalService?.jcd_key,
      issue_key: this.passgriddata[0]?.data?.ISSUE_KEY
    };

    this.assignPreparerService.getTemplates(
      this.resolveApiBase(),
      params,
      this.globalService
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.processTemplatesData(response.jsonObject);
        this.initView();
        this.showFormLoader = false;
      },
      (error: any) => {
        console.error('Error fetching templates:', error);
        this._snackBar.open('Error loading templates', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.showFormLoader = false;
      }
    );
  }

  processTemplatesData(jsonObject: any[]): void {
    this.templates = [];
    this.template_selection = [];

    let prev_temp_id = 0;
    let all_templates: any = {};
    let headerList: any[] = [];
    let temp_data_arr: any[] = [];
    let temp_display_Obj: any = {};
    let new_template_obj: any = {};

    const sorted_jsonObject = _.sortBy(jsonObject, ['TEMPLATE_KEY']);

    sorted_jsonObject.forEach((row: any, key: number) => {
      const temp_id = row.TEMPLATE_KEY;

      if (temp_id !== prev_temp_id) {
        if (prev_temp_id !== 0) {
          this.templates.push(all_templates);
          all_templates = {};
          headerList = [];
        }

        // Prepare dropdown data
        const temp_dropdown_data = {
          id: row.TEMPLATE_KEY,
          value: row.TEMPLATE_NAME
        };

        // Set new template key
        prev_temp_id = row.TEMPLATE_KEY;

        // Delete unwanted properties
        const tempDatakey = row.TEMPLATE_DATA_KEY;
        delete row.TEMPLATE_KEY;
        delete row.TEMPLATE_NAME;
        delete row.TEMPLATE_DATA_KEY;
        delete row.ATTRIB_NAME;
        delete row.ATTRIB_VALUE;
        delete row.DATE;
        delete row.PREPARER_SIGNATURE;

        // Create valid array of objects
        for (const col in row) {
          const attributes = row[col].split('~');
          const temp_table_obj: any = {
            id: attributes[0],
            description: attributes[4]
          };
          temp_table_obj[col] = attributes[5];
          temp_data_arr.push(temp_table_obj);
        }

        temp_data_arr.forEach((item: any) => {
          delete item.id;
          headerList.push(item.description);
          delete item.description;
          for (const col in item) {
            if (col === 'DATE') {
              item[col] = item[col] ? new Date(item[col]) : '';
            } else if (col === 'COUNTRY') {
              const tempDataId = item[col];
              item[col] = [];
              for (let i = 0; i < this.allCountries.length; i++) {
                if (this.allCountries[i].id === tempDataId) {
                  item[col] = [this.allCountries[i]];
                }
              }
              if (item[col].length < 1) {
                item[col] = [this.default_country];
              }
            } else if (col === 'STATE') {
              const tempDataId = item[col];
              item[col] = [];
              for (let i = 0; i < this.allUSStates.length; i++) {
                if (this.allUSStates[i].id === tempDataId) {
                  item[col] = [this.allUSStates[i]];
                }
              }
              if (temp_display_Obj.COUNTRY && temp_display_Obj.COUNTRY[0]?.id !== 'US') {
                item[col] = tempDataId;
              }
            } else if (col === 'PHONE_NO') {
              item[col] = item[col] ? parseInt(item[col]) : '';
            }
            temp_display_Obj[col] = item[col];
            new_template_obj[col] = "";
          }
        });

        temp_data_arr = [];
        temp_display_Obj.TEMPLATE_DATA_KEY = tempDatakey;
        temp_display_Obj.flag = false;
        temp_display_Obj.is_new = false;

        all_templates.tableHeaders = headerList;
        all_templates.newRowData = new_template_obj;
        new_template_obj = {};
        all_templates.dataRows = [];
        all_templates.dataRows.push(temp_display_Obj);
        temp_display_Obj = {};

        this.template_selection.push(temp_dropdown_data);

        if (jsonObject.length - 1 === key) {
          this.templates.push(all_templates);
          all_templates = {};
        }
      } else {
        prev_temp_id = row.TEMPLATE_KEY;

        // Delete unwanted properties
        const tempDatakey = row.TEMPLATE_DATA_KEY;
        delete row.TEMPLATE_KEY;
        delete row.TEMPLATE_NAME;
        delete row.TEMPLATE_DATA_KEY;
        delete row.ATTRIB_NAME;
        delete row.ATTRIB_VALUE;
        delete row.DATE;
        delete row.PREPARER_SIGNATURE;

        // Create valid array of objects
        for (const col in row) {
          const attributes = row[col].split('~');
          const temp_table_obj: any = {
            id: attributes[0],
            description: attributes[4]
          };
          temp_table_obj[col] = attributes[5];
          temp_data_arr.push(temp_table_obj);
        }

        temp_data_arr.forEach((item: any) => {
          delete item.id;
          delete item.description;
          for (const col in item) {
            if (col === 'DATE') {
              item[col] = item[col] ? new Date(item[col]) : '';
            } else if (col === 'COUNTRY') {
              const tempDataId = item[col];
              item[col] = [];
              for (let i = 0; i < this.allCountries.length; i++) {
                if (this.allCountries[i].id === tempDataId) {
                  item[col] = [this.allCountries[i]];
                }
              }
              if (item[col].length < 1) {
                item[col] = [this.default_country];
              }
            } else if (col === 'STATE') {
              const tempDataId = item[col];
              item[col] = [];
              for (let i = 0; i < this.allUSStates.length; i++) {
                if (this.allUSStates[i].id === tempDataId) {
                  item[col] = [this.allUSStates[i]];
                }
              }
              if (temp_display_Obj.COUNTRY && temp_display_Obj.COUNTRY[0]?.id !== 'US') {
                item[col] = tempDataId;
              }
            } else if (col === 'PHONE_NO') {
              item[col] = item[col] ? parseInt(item[col]) : '';
            }
            temp_display_Obj[col] = item[col];
          }
        });

        temp_data_arr = [];
        temp_display_Obj.TEMPLATE_DATA_KEY = tempDatakey;
        temp_display_Obj.flag = false;
        temp_display_Obj.is_new = false;

        all_templates.dataRows.push(temp_display_Obj);
        temp_display_Obj = {};

        if (jsonObject.length - 1 === key) {
          this.templates.push(all_templates);
          all_templates = {};
        }
      }
    });
  }

  initView(): void {
    if (this.template_selection.length > 0) {
      this.selectedTemplate = this.template_selection[0];
      this.display_template = this.templates[0];
      this.display_template_copy = _.cloneDeep(this.display_template);
    }
  }

  removeLeid(index: number): void {
    this.leid_list.splice(index, 1);
  }

  addRows(): void {
    const newRow = _.cloneDeep(this.display_template.newRowData);
    newRow.TEMPLATE_DATA_KEY = 0;
    newRow.COUNTRY = [this.default_country];
    newRow.STATE = [];
    newRow.is_new = true;
    newRow.flag = false;
    this.display_template.dataRows.push(newRow);
  }

  removeRow(index: number): void {
    this.display_template.dataRows.splice(index, 1);
  }

  loadTemplate(): void {
    if (this.templates.length === 1) {
      this.display_template = this.templates[0];
    } else {
      this.display_template = this.templates[this.selectedTemplate.id - 1];
    }
    this.display_template_copy = _.cloneDeep(this.display_template);
  }

  checkedRow(index: number, row: any, state: boolean): void {
    this.selectedRowIndex = index;
    this.saveBtn = !state;
    this.selectedRow = row;

    // Uncheck other rows
    this.display_template.dataRows.forEach((tem: any, key: number) => {
      if (index !== key) {
        this.display_template.dataRows[key].flag = false;
      }
    });
  }

  onCountrySelection(templateDataKey: any): void {
    const index = _.findIndex(this.display_template.dataRows, { TEMPLATE_DATA_KEY: templateDataKey });
    this.selectedRowIndex = index;
  }

  /**
   * Get row keys in the correct order (maintaining insertion order like AngularJS)
   * Excludes internal properties: TEMPLATE_DATA_KEY, flag, is_new
   */
  getRowKeys(row: any): string[] {
    if (!row) return [];
    return Object.keys(row).filter(key =>
      key !== 'TEMPLATE_DATA_KEY' && key !== 'flag' && key !== 'is_new'
    );
  }

  /**
   * Get selected country ID from the array format
   */
  getSelectedCountryId(countryArray: any): string {
    if (Array.isArray(countryArray) && countryArray.length > 0) {
      return countryArray[0].id;
    }
    return '';
  }

  /**
   * Get selected state ID from the array format
   */
  getSelectedStateId(stateValue: any): string {
    if (Array.isArray(stateValue) && stateValue.length > 0) {
      return stateValue[0].id;
    }
    // For non-US countries, state might be a string
    if (typeof stateValue === 'string') {
      return stateValue;
    }
    return '';
  }

  /**
   * Handle country selection change
   */
  onCountrySelect(index: number, row: any, countryId: string): void {
    // Find the country object
    const country = this.allCountries.find(c => c.id === countryId);
    if (country) {
      row.COUNTRY = [country];

      // Update state based on country
      if (countryId === 'US') {
        // Set to empty array so "Select" option shows by default
        row.STATE = [];
      } else {
        row.STATE = '';
      }

      // Call existing change handler
      this.onCountryChange(index, row.COUNTRY);
    }
  }

  /**
   * Handle state selection change
   */
  onStateSelect(row: any, stateId: string): void {
    // Find the state object
    const state = this.allUSStates.find(s => s.id === stateId);
    if (state) {
      row.STATE = [state];
    }
  }

  onCountryChange(index: number, event: any): void {
    const row = this.display_template.dataRows[index];
    if (event && event.length > 0) {
      if (event[0].id === 'US') {
        // Set to empty array so "Select" option shows by default
        row.STATE = [];
      } else {
        row.STATE = '';
      }
    } else {
      row.STATE = '';
    }
  }

  onDateChange(row: any, key: string, event: any): void {
    row[key] = event ? new Date(event) : '';
  }

  isValidDate(dateObject: any): boolean {
    return new Date(dateObject).toString() === 'Invalid Date';
  }

  save(): void {
    const baseUrl = this.resolveApiBase();
    if (this.crudLoading || !baseUrl) return;

    // Validation
    if (this.leid_list.length === 0) {
      this._snackBar.open('Cannot assign preparer, please select atleast one LEID', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (_.filter(this.display_template_copy.dataRows, { PREPARER_NAME: this.selectedRow.PREPARER_NAME }).length && this.selectedRow.is_new) {
      this._snackBar.open(`Preparer already exists with the name, "${this.selectedRow.PREPARER_NAME}" please provide a different name`, 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    // Field validation based on country
    const errorFields: string[] = [];
    const mainData: any = _.cloneDeep(this.selectedRow);
    const country_US = ['ADDRESS_LINE_1', 'CITY', 'STATE', 'ZIP'];
    const country_non_US = ['ADDRESS_LINE_1'];

    if (mainData.COUNTRY && mainData.COUNTRY.length === 1) {
      if (mainData.COUNTRY[0].id === 'US') {
        country_US.forEach((val: string) => {
          if (Array.isArray(mainData[val])) {
            if (mainData[val].length === 0) {
              errorFields.push(val.split('_').join(' '));
            }
          } else {
            if (!mainData[val]) {
              errorFields.push(val.split('_').join(' '));
            }
          }
        });
      } else {
        country_non_US.forEach((val: string) => {
          if (!mainData[val]) {
            errorFields.push(val.split('_').join(' '));
          }
        });
      }
    }

    if (errorFields.length >= 1) {
      this._snackBar.open(`Following fields are Mandatory: ${errorFields.join(', ')}`, 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    // Prepare data for save
    this.isSaveClicked = true;
    this.saveloader = true;
    this.crudLoading = true;

    mainData.COUNTRY = mainData.COUNTRY.length ? mainData.COUNTRY[0].id : '';
    if (Array.isArray(mainData.STATE)) {
      mainData.STATE = mainData.STATE.length > 0 ? mainData.STATE[0].id : '';
    }

    delete mainData.flag;
    mainData.TEMPLATE_KEY = this.selectedTemplate.id;

    this.combination_key_List = [];
    this.leid_list.forEach((val: any) => {
      this.combination_key_List.push(val.combination_key);
    });

    mainData.COMBINATION_KEY = this.combination_key_List.join(',');
    mainData.GROUP_OBJ_KEY = this.leid_list[0].group_obj_key;
    mainData.DATE = '';
    mainData.PREPARER_SIGNATURE = '';
    mainData.ISSUE_KEY = this.passgriddata[0]?.data?.ISSUE_KEY;

    const jsonSettings = {
      tax_year: this.passgriddata[0]?.data?.TAX_YEAR,
      scenario: this.passgriddata[0]?.data?.SCENARIO,
      jcd_key: this.passgriddata[0]?.data?.JCD_KEY,
      combination_key: this.passgriddata[0]?.data?.COMBINATION_KEY
    };

    // Call save service
    this.assignPreparerService.saveAssignPreparer(
      this.resolveApiBase(),
      [mainData],
      jsonSettings,
      this.globalService,
      this.filterParams,
      this.leid_list  // Pass leid_list instead of rowData for checkLockedbj construction
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.saveloader = false;
        this.crudLoading = false;

        if (response.callSuccess === '1' && response.errorMessage === 'null') {
          this._snackBar.open('Template Assigned successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        } else {
          if (response && response.errorMessage && response.errorMessage !== 'null') {
            this._snackBar.open(response.errorMessage, 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          } else {
            this._snackBar.open('Error occurred in saving the Transaction. If this continues please contact support', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          }
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.saveloader = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  reset(): void {
    this.display_template = _.cloneDeep(this.display_template_copy);
    this.selectedRow = {};
    this.saveBtn = true;
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
